import axiosInstance from '../../../config/axios';

export type CrmTaskStatus = 'OPEN' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED';
export type CrmTaskPriority = 'LOW' | 'NORMAL' | 'HIGH';

export interface CrmTask {
  id: number;
  title: string;
  description: string;
  status: CrmTaskStatus;
  priority: CrmTaskPriority;
  due_date: string | null;

  // Partner - backend may return either partner or partner_id
  partner?: number | null;
  partner_id?: number | null;
  partner_name?: string | null;

  // Project - backend may return either project or project_id
  project?: number | null;
  project_id?: number | null;
  project_name?: string | null;

  // Offer - backend may return either offer or offer_id
  offer?: number | null;
  offer_id?: number | null;
  offer_number?: string | null;

  // Invoice
  invoice?: number | null;
  invoice_id?: number | null;
  invoice_number?: string | null;

  // Payment Milestone - backend may return either payment_milestone or payment_milestone_id
  payment_milestone?: number | null;
  payment_milestone_id?: number | null;
  payment_milestone_label?: string | null;

  assigned_to: number | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface CrmTaskListParams {
  status?: string;
  partner_id?: number;
  project_id?: number;
  offer_id?: number;
  invoice_id?: number;
  payment_milestone?: number;
  due_from?: string;
  due_to?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

export interface CreateCrmTaskPayload {
  title: string;
  description?: string;
  status?: CrmTaskStatus;
  priority?: CrmTaskPriority;
  due_date?: string | null;
  partner?: number | null;
  project?: number | null;
  offer?: number | null;
  invoice?: number | null;
  payment_milestone?: number | null;
  assigned_to?: number | null;
}

export interface UpdateCrmTaskPayload extends Partial<CreateCrmTaskPayload> {}

export interface TaskComment {
  id: number;
  task: number;
  text: string;
  author_id: number | null;
  author_name?: string;
  created_at: string;
  updated_at: string;
}

export interface TaskAttachment {
  id: number;
  task: number;
  comment?: number | null;
  file_name: string;
  file_size: number;
  content_type: string;
  file_url: string;
  uploaded_by: number | null;
  uploaded_by_name?: string;
  created_at: string;
}

export interface CreateCommentPayload {
  text: string;
}

export interface BulkStatusChangeResult {
  id: number;
  success: boolean;
  error?: string;
}

export interface BulkStatusChangeResponse {
  status: CrmTaskStatus;
  updated_count: number;
  total_requested: number;
  results: BulkStatusChangeResult[];
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const CRM_TASK_STATUS_OPTIONS: { value: CrmTaskStatus; label: string }[] = [
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'DONE', label: 'Done' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

export const CRM_TASK_PRIORITY_OPTIONS: { value: CrmTaskPriority; label: string }[] = [
  { value: 'LOW', label: 'Low' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'HIGH', label: 'High' },
];

const BASE = '/api/admin/crm-tasks/';

export default class CrmTaskService {
  static async list(params?: CrmTaskListParams): Promise<CrmTask[]> {
    const queryParams: Record<string, string> = {};

    if (params?.status) queryParams.status = params.status;
    if (params?.partner_id) queryParams.partner_id = String(params.partner_id);
    if (params?.project_id) queryParams.project_id = String(params.project_id);
    if (params?.offer_id) queryParams.offer_id = String(params.offer_id);
    if (params?.invoice_id) queryParams.invoice_id = String(params.invoice_id);
    if (params?.payment_milestone) queryParams.payment_milestone_id = String(params.payment_milestone);
    if (params?.due_from) queryParams.due_from = params.due_from;
    if (params?.due_to) queryParams.due_to = params.due_to;
    if (params?.search) queryParams.search = params.search;
    if (params?.page) queryParams.page = String(params.page);
    if (params?.page_size) queryParams.page_size = String(params.page_size);

    const query = new URLSearchParams(queryParams).toString();
    const url = query ? `${BASE}?${query}` : BASE;

    const response = await axiosInstance.get<CrmTask[] | PaginatedResponse<CrmTask>>(url);

    if (Array.isArray(response.data)) {
      return response.data;
    }
    return response.data.results;
  }

  static async get(id: number): Promise<CrmTask> {
    const response = await axiosInstance.get<CrmTask>(`${BASE}${id}/`);
    return response.data;
  }

  static async create(payload: CreateCrmTaskPayload): Promise<CrmTask> {
    const response = await axiosInstance.post<CrmTask>(BASE, payload);
    return response.data;
  }

  static async update(id: number, payload: UpdateCrmTaskPayload): Promise<CrmTask> {
    const response = await axiosInstance.patch<CrmTask>(`${BASE}${id}/`, payload);
    return response.data;
  }

  static async delete(id: number): Promise<void> {
    await axiosInstance.delete(`${BASE}${id}/`);
  }

  static async changeStatus(id: number, status: CrmTaskStatus): Promise<CrmTask> {
    const response = await axiosInstance.post<CrmTask>(`${BASE}${id}/change-status/`, { status });
    return response.data;
  }

  static async bulkChangeStatus(ids: number[], status: CrmTaskStatus): Promise<BulkStatusChangeResponse> {
    const response = await axiosInstance.post<BulkStatusChangeResponse>(
      `${BASE}bulk-change-status/`,
      { ids, status }
    );
    return response.data;
  }

  static getStatusLabel(status: CrmTaskStatus): string {
    const option = CRM_TASK_STATUS_OPTIONS.find((opt) => opt.value === status);
    return option?.label || status;
  }

  static getStatusColor(status: CrmTaskStatus): { bg: string; text: string } {
    switch (status) {
      case 'OPEN':
        return { bg: '#dbeafe', text: '#1e40af' };
      case 'IN_PROGRESS':
        return { bg: '#fef3c7', text: '#92400e' };
      case 'DONE':
        return { bg: '#d1fae5', text: '#065f46' };
      case 'CANCELLED':
        return { bg: '#f3f4f6', text: '#374151' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  }

  static getPriorityLabel(priority: CrmTaskPriority): string {
    const option = CRM_TASK_PRIORITY_OPTIONS.find((opt) => opt.value === priority);
    return option?.label || priority;
  }

  static getPriorityColor(priority: CrmTaskPriority): { bg: string; text: string } {
    switch (priority) {
      case 'HIGH':
        return { bg: '#fee2e2', text: '#991b1b' };
      case 'NORMAL':
        return { bg: '#e0e7ff', text: '#3730a3' };
      case 'LOW':
        return { bg: '#f3f4f6', text: '#374151' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  }

  static formatDueDate(dueDate: string | null): string {
    if (!dueDate) return '-';

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);

    const diffDays = Math.floor((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';

    return new Date(dueDate).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
    });
  }

  static isOverdue(dueDate: string | null): boolean {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return due < today;
  }

  static isDueToday(dueDate: string | null): boolean {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return due.getTime() === today.getTime();
  }

  static getDefaultDueDate(): string {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  }

  // ─────────────────────────────────────────────────────────────
  // Comments
  // ─────────────────────────────────────────────────────────────

  static async listComments(taskId: number): Promise<TaskComment[]> {
    const response = await axiosInstance.get<TaskComment[]>(`${BASE}${taskId}/comments/`);
    return response.data;
  }

  static async createComment(taskId: number, payload: CreateCommentPayload): Promise<TaskComment> {
    const response = await axiosInstance.post<TaskComment>(`${BASE}${taskId}/comments/`, payload);
    return response.data;
  }

  static async deleteComment(commentId: number): Promise<void> {
    await axiosInstance.delete(`/api/admin/crm-task-comments/${commentId}/`);
  }

  // ─────────────────────────────────────────────────────────────
  // Attachments
  // ─────────────────────────────────────────────────────────────

  static async listAttachments(taskId: number): Promise<TaskAttachment[]> {
    const response = await axiosInstance.get<TaskAttachment[]>(`${BASE}${taskId}/attachments/`);
    return response.data;
  }

  static async uploadAttachment(taskId: number, file: File, commentId?: number): Promise<TaskAttachment> {
    const formData = new FormData();
    formData.append('file', file);
    if (commentId) {
      formData.append('comment', String(commentId));
    }

    const response = await axiosInstance.post<TaskAttachment>(
      `${BASE}${taskId}/attachments/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  static async deleteAttachment(attachmentId: number): Promise<void> {
    await axiosInstance.delete(`/api/admin/crm-task-attachments/${attachmentId}/`);
  }

  // ─────────────────────────────────────────────────────────────
  // Utility: Format relative time
  // ─────────────────────────────────────────────────────────────

  static formatRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
}
